import React, { useEffect, useState } from "react";
import { Link, Navigate, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import { useApp } from "../lib/ctx";
import { LogOut, LayoutDashboard, FileText, Tag, Zap, BarChart2, MessageSquare, Users, Film, DollarSign, Trophy, Upload, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const CoverImageUploader = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = React.useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please pick an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { setErr("Image too large (max 8 MB)."); return; }
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed. Try again.");
    } finally { setUploading(false); }
  };
  const onDrop = (e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); };
  const preview = resolveImg(value);
  return (
    <div data-testid="cover-uploader">
      {preview ? (
        <div className="relative rounded-xl overflow-hidden border border-border">
          <img src={preview} alt="cover preview" className="w-full aspect-[16/10] object-cover" />
          <div className="absolute top-2 right-2 flex gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} data-testid="change-image"
              className="h-9 px-3 rounded-full bg-background/90 backdrop-blur text-xs font-bold inline-flex items-center gap-1 border border-border">
              <Upload className="w-3 h-3" />{uploading?"Uploading…":"Change"}
            </button>
            <button type="button" onClick={() => onChange("")} data-testid="clear-image"
              className="w-9 h-9 rounded-full bg-background/90 backdrop-blur flex items-center justify-center border border-border">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <div onDrop={onDrop} onDragOver={(e)=>e.preventDefault()} onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
          className="rounded-xl border-2 border-dashed border-border hover:border-signal cursor-pointer p-8 text-center bg-background transition">
          <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
          <div className="font-display font-bold mt-2">Drop an image, or click to pick</div>
          <div className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP or GIF · up to 8 MB</div>
          {uploading && <div className="text-xs font-bold text-signal mt-2">Uploading…</div>}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="upload-input"
        onChange={e => upload(e.target.files?.[0])} />
      {err && <div className="text-signal text-xs mt-2" data-testid="upload-error">{err}</div>}
    </div>
  );
};

export const AdminLogin = () => {
  const { login, user } = useApp();
  const [email, setEmail] = useState("admin@newsgram.bd");
  const [password, setPassword] = useState("newsgram2026");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  if (user) return <Navigate to="/admin/dashboard" replace />;
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); nav("/admin/dashboard"); }
    catch (e) { setErr(e.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="admin-login">
      <div className="w-full max-w-md rounded-3xl bg-card border border-border p-8 card-soft">
        <div className="flex items-center gap-1.5 mb-6"><span className="font-display font-extrabold text-3xl">Newsgram</span><span className="w-2 h-2 rounded-full bg-signal mt-2" /></div>
        <h1 className="font-display font-extrabold text-2xl">Admin sign-in</h1>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input required type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" data-testid="admin-email" className="w-full h-12 px-5 rounded-full bg-background border border-border outline-none focus:border-signal" />
          <input required type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password" data-testid="admin-password" className="w-full h-12 px-5 rounded-full bg-background border border-border outline-none focus:border-signal" />
          {err && <div className="text-signal text-sm">{err}</div>}
          <button data-testid="admin-login-submit" className="w-full h-12 rounded-full bg-signal text-white font-bold">{loading?"Signing in…":"Sign in"}</button>
        </form>
        <p className="text-xs text-muted-foreground mt-4">Demo: admin@newsgram.bd / newsgram2026</p>
      </div>
    </div>
  );
};

const AdminShell = ({ children }) => {
  const { user, logout } = useApp();
  const loc = useLocation();
  if (!user) return <Navigate to="/admin" replace />;
  const items = [
    { to: "/admin/dashboard", label: "Dashboard", Icon: LayoutDashboard },
    { to: "/admin/stories", label: "Stories", Icon: FileText },
    { to: "/admin/media", label: "Ticker & Market", Icon: Zap },
    { to: "/admin/scores", label: "Scores", Icon: Trophy },
    { to: "/admin/videos", label: "Videos", Icon: Film },
    { to: "/admin/subscribers", label: "Subscribers", Icon: Users },
    { to: "/admin/tips", label: "Tips", Icon: MessageSquare },
    { to: "/admin/ads", label: "Ad inquiries", Icon: DollarSign },
    { to: "/admin/analytics", label: "Analytics", Icon: BarChart2 },
  ];
  return (
    <div className="min-h-screen flex" data-testid="admin-shell">
      <aside className="hidden md:flex flex-col w-60 border-r border-border bg-card p-4 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-1.5 mb-6"><span className="font-display font-extrabold text-xl">Newsgram</span><span className="w-1.5 h-1.5 rounded-full bg-signal mt-1" /></Link>
        <nav className="flex-1 space-y-1">
          {items.map(({to, label, Icon}) => (
            <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g,'-')}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${loc.pathname.startsWith(to) ? "bg-foreground text-background" : "hover:bg-muted"}`}>
              <Icon className="w-4 h-4" />{label}
            </Link>
          ))}
        </nav>
        <div className="mt-auto">
          <div className="text-xs text-muted-foreground mb-2">{user.email} · {user.role}</div>
          <button onClick={logout} data-testid="admin-logout" className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-full border border-border text-sm font-bold hover:bg-muted"><LogOut className="w-3 h-3" />Sign out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden"><div className="max-w-6xl mx-auto p-6">{children}</div></main>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/analytics").then(r => setStats(r.data)); }, []);
  const cards = [
    { label: "Published today", v: stats.today, k: "today" },
    { label: "Drafts", v: stats.drafts, k: "drafts" },
    { label: "In review", v: stats.review, k: "review" },
    { label: "Subscribers", v: stats.subscribers, k: "subs" },
    { label: "New tips", v: stats.tips, k: "tips" },
    { label: "Ad inquiries", v: stats.ads, k: "ads" },
  ];
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display font-extrabold text-3xl">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
        {cards.map(c => (
          <div key={c.k} className="rounded-2xl bg-card border border-border p-4" data-testid={`stat-${c.k}`}>
            <div className="text-xs text-muted-foreground font-semibold">{c.label}</div>
            <div className="font-display font-extrabold text-3xl mt-1">{c.v ?? "—"}</div>
          </div>
        ))}
      </div>
      <h2 className="font-display font-extrabold text-xl mt-10">Top stories (24h)</h2>
      <div className="mt-3 rounded-2xl bg-card border border-border divide-y divide-border">
        {(stats.top_24h || []).map(s => (
          <div key={s.id} className="p-4 flex items-center justify-between">
            <Link to={`/story/${s.slug}`} className="text-sm font-semibold hover:text-signal truncate">{s.title}</Link>
            <span className="text-xs font-bold text-muted-foreground">{s.view_count} views</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StoriesList = () => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const nav = useNavigate();
  const load = () => api.get("/admin/stories", { params: filter ? { status: filter } : {} }).then(r => setList(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  return (
    <div data-testid="admin-stories">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-3xl">Stories</h1>
        <button onClick={() => nav("/admin/stories/new")} data-testid="new-story-btn" className="h-10 px-4 rounded-full bg-signal text-white font-bold">+ New story</button>
      </div>
      <div className="flex gap-2 mt-4">
        {["","draft","review","published"].map(f => (
          <button key={f||"all"} onClick={() => setFilter(f)} data-testid={`filter-${f||"all"}`}
            className={`h-9 px-3 rounded-full text-xs font-bold border ${filter===f ? "bg-foreground text-background border-foreground" : "border-border"}`}>{f||"All"}</button>
        ))}
      </div>
      <div className="mt-5 rounded-2xl bg-card border border-border divide-y divide-border">
        {list.map(s => (
          <div key={s.id} className="p-4 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{s.title}</div>
              <div className="text-xs text-muted-foreground">{s.status} · {s.slug}</div>
            </div>
            <button onClick={() => nav(`/admin/stories/${s.id}`)} data-testid={`edit-${s.slug}`} className="h-9 px-3 rounded-full border border-border text-xs font-bold">Edit</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const EMPTY_STORY = { title:"", title_bn:"", slug:"", language:"en", gist:["","",""], gist_bn:["","",""], why_it_matters:"", whats_next:"", body:"", category_id:"", tags:[], cover_image:"", image_credit:"", alt_text:"", sources:[{publication:"",url:""}], status:"draft", is_breaking:false, is_featured:false, in_60_seconds:false, is_sponsored:false, sponsor_name:"", is_explainer:false, seo_title:"", seo_description:"" };

const StoryEditor = ({ storyId }) => {
  const [story, setStory] = useState(EMPTY_STORY);
  const [cats, setCats] = useState([]);
  const [tab, setTab] = useState("en");
  const [aiKind, setAiKind] = useState("headlines");
  const [aiNotes, setAiNotes] = useState("");
  const [aiOut, setAiOut] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data));
    if (storyId && storyId !== "new") api.get(`/admin/stories/id/${storyId}`).then(r => setStory({ ...EMPTY_STORY, ...r.data, gist: r.data.gist?.length ? r.data.gist : ["","",""], gist_bn: r.data.gist_bn?.length ? r.data.gist_bn : ["","",""], sources: r.data.sources?.length ? r.data.sources : [{publication:"",url:""}] }));
  }, [storyId]);

  const set = (k, v) => setStory(s => ({ ...s, [k]: v }));
  const setGist = (i, v, bn=false) => setStory(s => { const g = [...(bn ? s.gist_bn : s.gist)]; g[i] = v; return { ...s, [bn?"gist_bn":"gist"]: g }; });
  const addSource = () => setStory(s => ({ ...s, sources: [...s.sources, {publication:"",url:""}] }));
  const setSource = (i, k, v) => setStory(s => { const sr = [...s.sources]; sr[i] = {...sr[i], [k]: v}; return { ...s, sources: sr }; });

  const save = async (status) => {
    setErr("");
    const payload = { ...story, status, gist: story.gist.filter(Boolean), gist_bn: story.gist_bn.filter(Boolean) };
    try {
      if (storyId && storyId !== "new") await api.patch(`/admin/stories/${storyId}`, payload);
      else { const r = await api.post("/admin/stories", payload); nav(`/admin/stories/${r.data.id}`, { replace: true }); }
      if (status === "published") nav("/admin/stories");
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };

  const runAI = async () => {
    setAiLoading(true); setAiOut("");
    try { const r = await api.post("/admin/ai/generate", { kind: aiKind, source_notes: aiNotes, platform: "instagram" }); setAiOut(r.data.text); }
    catch (e) { setAiOut("Error: " + (e.response?.data?.detail || "ai failed")); }
    finally { setAiLoading(false); }
  };

  const titleOver = story.title.length > 90;

  return (
    <div data-testid="story-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display font-extrabold text-2xl">{storyId==="new"?"New story":"Edit story"}</h1>
        <div className="flex gap-2">
          <button onClick={() => save("draft")} data-testid="save-draft" className="h-10 px-4 rounded-full border border-border font-bold text-sm">Save draft</button>
          <button onClick={() => save("review")} data-testid="save-review" className="h-10 px-4 rounded-full bg-foreground text-background font-bold text-sm">Send to review</button>
          <button onClick={() => save("published")} data-testid="save-publish" className="h-10 px-4 rounded-full bg-signal text-white font-bold text-sm">Publish</button>
        </div>
      </div>
      {err && <div className="mt-3 rounded-xl bg-signal/10 border border-signal text-signal text-sm p-3" data-testid="editor-error">{err}</div>}
      <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            {["en","bn"].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className={`h-9 px-4 rounded-full text-xs font-bold ${tab===t?"bg-foreground text-background":"border border-border"}`}>{t==="en"?"English":"বাংলা"}</button>
            ))}
          </div>
          <div>
            <label className="text-xs font-bold">Headline ({(tab==="en"?story.title:story.title_bn).length}/90)</label>
            <input value={tab==="en"?story.title:story.title_bn} onChange={e=>set(tab==="en"?"title":"title_bn", e.target.value)} data-testid="title-input"
              className={`w-full p-3 rounded-xl bg-card border font-display font-extrabold text-2xl ${titleOver?"border-signal":"border-border"}`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Slug (auto)" value={story.slug} onChange={e=>set("slug", e.target.value)} data-testid="slug-input" className="h-11 px-4 rounded-full bg-card border border-border" />
            <select value={story.category_id} onChange={e=>set("category_id", e.target.value)} data-testid="category-select" className="h-11 px-4 rounded-full bg-card border border-border">
              <option value="">Category…</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold">The Gist (3 bullets, 140 chars each)</label>
            {[0,1,2].map(i => (
              <input key={i} value={(tab==="en"?story.gist:story.gist_bn)[i] || ""} onChange={e=>setGist(i, e.target.value, tab==="bn")} data-testid={`gist-${i}`}
                placeholder={`Gist ${i+1}`} className="w-full mt-2 h-11 px-4 rounded-xl bg-card border border-border" />
            ))}
          </div>
          <div><label className="text-xs font-bold">Why it matters (max 220)</label>
            <textarea rows={2} value={story.why_it_matters} onChange={e=>set("why_it_matters", e.target.value)} data-testid="why-matters" className="w-full p-3 rounded-xl bg-card border border-border" /></div>
          <div><label className="text-xs font-bold">What's next (optional)</label>
            <textarea rows={2} value={story.whats_next} onChange={e=>set("whats_next", e.target.value)} data-testid="whats-next" className="w-full p-3 rounded-xl bg-card border border-border" /></div>
          <div><label className="text-xs font-bold">Read deeper (optional)</label>
            <textarea rows={5} value={story.body} onChange={e=>set("body", e.target.value)} data-testid="body" className="w-full p-3 rounded-xl bg-card border border-border" /></div>

          <div className="rounded-2xl border border-border p-4 space-y-3">
            <div className="font-display font-bold">Cover image</div>
            <CoverImageUploader value={story.cover_image} onChange={(v)=>set("cover_image", v)} />
            <input placeholder="…or paste an image URL" value={story.cover_image} onChange={e=>set("cover_image", e.target.value)} data-testid="cover-url" className="w-full h-11 px-4 rounded-full bg-background border border-border" />
            <input placeholder="Image credit (required)" value={story.image_credit} onChange={e=>set("image_credit", e.target.value)} data-testid="credit" className="w-full h-11 px-4 rounded-full bg-background border border-border" />
            <input placeholder="Alt text" value={story.alt_text} onChange={e=>set("alt_text", e.target.value)} data-testid="alt-text" className="w-full h-11 px-4 rounded-full bg-background border border-border" />
          </div>

          <div className="rounded-2xl border border-border p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold">Sources</div>
              <button onClick={addSource} className="text-xs font-bold text-signal" data-testid="add-source">+ Add source</button>
            </div>
            {story.sources.map((s, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <input placeholder="Publication" value={s.publication} onChange={e=>setSource(i,"publication",e.target.value)} data-testid={`source-pub-${i}`} className="h-10 px-3 rounded-lg bg-background border border-border text-sm" />
                <input placeholder="URL" value={s.url} onChange={e=>setSource(i,"url",e.target.value)} data-testid={`source-url-${i}`} className="h-10 px-3 rounded-lg bg-background border border-border text-sm" />
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-border p-4 grid grid-cols-2 gap-3 text-sm">
            {[["is_breaking","Breaking"],["is_featured","Featured"],["in_60_seconds","In 60s"],["is_sponsored","Sponsored"],["is_explainer","Explainer"]].map(([k,l]) => (
              <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={!!story[k]} onChange={e=>set(k, e.target.checked)} data-testid={`flag-${k}`} /> {l}</label>
            ))}
            {story.is_sponsored && <input placeholder="Sponsor name" value={story.sponsor_name} onChange={e=>set("sponsor_name", e.target.value)} className="h-10 px-3 col-span-2 rounded-lg bg-background border border-border" />}
          </div>

          <div className="rounded-2xl border border-border p-4 space-y-2">
            <div className="font-display font-bold">AI assistant (Claude Sonnet 5.5)</div>
            <div className="flex flex-wrap gap-2">
              {["headlines","gist","why_matters","bangla","caption"].map(k => (
                <button key={k} onClick={()=>setAiKind(k)} data-testid={`ai-kind-${k}`} className={`h-8 px-3 rounded-full text-xs font-bold ${aiKind===k?"bg-foreground text-background":"border border-border"}`}>{k}</button>
              ))}
            </div>
            <textarea rows={3} placeholder="Paste source notes…" value={aiNotes} onChange={e=>setAiNotes(e.target.value)} data-testid="ai-notes" className="w-full p-3 rounded-xl bg-background border border-border text-sm" />
            <button onClick={runAI} disabled={aiLoading || !aiNotes} data-testid="ai-run" className="h-10 px-4 rounded-full bg-signal text-white font-bold text-sm disabled:opacity-50">{aiLoading?"Thinking…":"Generate"}</button>
            {aiOut && <div className="mt-2 rounded-xl bg-background border border-border p-3 whitespace-pre-wrap text-sm" data-testid="ai-output">{aiOut}</div>}
          </div>
        </div>

        {/* Live mobile preview */}
        <aside className="lg:sticky lg:top-6 self-start">
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Live preview · 390px</div>
          <div className="rounded-3xl border-4 border-foreground overflow-hidden bg-background w-full max-w-[320px] mx-auto">
            {story.cover_image && <img src={resolveImg(story.cover_image)} alt="" className="w-full aspect-[16/10] object-cover" />}
            <div className="p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-signal">{cats.find(c=>c.id===story.category_id)?.name || "Category"}</div>
              <h3 className="font-display font-extrabold text-xl mt-2 leading-tight">{story.title || "Your headline here"}</h3>
              <div className="mt-3 rounded-xl bg-muted p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-signal">The Gist</div>
                <ul className="mt-1 space-y-1 text-xs">
                  {story.gist.filter(Boolean).map((g,i)=><li key={i}>• {g}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const StoryEditorWrap = () => {
  const id = window.location.pathname.split("/").pop();
  return <StoryEditor storyId={id} key={id} />;
};

const MediaMgmt = () => {
  const [ticker, setTicker] = useState([]);
  const [market, setMarket] = useState({});
  const [tForm, setTForm] = useState({ text: "", url: "" });
  const [mForm, setMForm] = useState({ dsex: "", usd_bdt: "", gold: "" });
  const load = async () => {
    setTicker((await api.get("/ticker")).data);
    setMarket((await api.get("/market")).data);
  };
  useEffect(() => { load(); }, []);
  const addTicker = async (e) => { e.preventDefault(); await api.post("/admin/ticker", { ...tForm, active: true }); setTForm({ text:"", url:"" }); load(); };
  const delTicker = async (id) => { await api.delete(`/admin/ticker/${id}`); load(); };
  const saveMarket = async (e) => { e.preventDefault(); await api.post("/admin/market", mForm); setMForm({ dsex:"", usd_bdt:"", gold:""}); load(); };
  return (
    <div data-testid="admin-media">
      <h1 className="font-display font-extrabold text-3xl">Ticker & Market</h1>
      <section className="mt-6">
        <h2 className="font-display font-bold text-xl mb-2">Breaking ticker</h2>
        <form onSubmit={addTicker} className="flex gap-2 mb-3"><input required placeholder="Headline" value={tForm.text} onChange={e=>setTForm(f=>({...f, text:e.target.value}))} className="flex-1 h-10 px-4 rounded-full border border-border bg-card" data-testid="ticker-text" /><button className="h-10 px-4 rounded-full bg-signal text-white font-bold text-sm">Add</button></form>
        <div className="rounded-2xl bg-card border border-border divide-y divide-border">
          {ticker.map(t => <div key={t.id} className="p-3 flex items-center justify-between gap-3"><span className="text-sm">{t.text}</span><button onClick={()=>delTicker(t.id)} className="text-xs text-signal font-bold">Delete</button></div>)}
        </div>
      </section>
      <section className="mt-10">
        <h2 className="font-display font-bold text-xl mb-2">Market snapshot</h2>
        <div className="text-sm text-muted-foreground mb-3">Current: DSEX {market.dsex || "—"} · USD/BDT {market.usd_bdt || "—"} · Gold {market.gold || "—"}</div>
        <form onSubmit={saveMarket} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input placeholder="DSEX e.g. 6,342.21 ▲ 0.8%" value={mForm.dsex} onChange={e=>setMForm(f=>({...f, dsex:e.target.value}))} className="h-10 px-4 rounded-full border border-border bg-card" data-testid="market-dsex" />
          <input placeholder="USD/BDT" value={mForm.usd_bdt} onChange={e=>setMForm(f=>({...f, usd_bdt:e.target.value}))} className="h-10 px-4 rounded-full border border-border bg-card" data-testid="market-usd" />
          <input placeholder="Gold" value={mForm.gold} onChange={e=>setMForm(f=>({...f, gold:e.target.value}))} className="h-10 px-4 rounded-full border border-border bg-card" data-testid="market-gold" />
          <button className="h-10 px-4 rounded-full bg-foreground text-background font-bold text-sm sm:col-span-3">Save market snapshot</button>
        </form>
      </section>
    </div>
  );
};

const ScoresMgmt = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ sport: "cricket", teams: "", status: "LIVE", details: "" });
  const load = () => api.get("/scores").then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/admin/scores", form); setForm({ sport:"cricket", teams:"", status:"LIVE", details:""}); load(); };
  const del = async (id) => { await api.delete(`/admin/scores/${id}`); load(); };
  return (
    <div data-testid="admin-scores">
      <h1 className="font-display font-extrabold text-3xl">Live scores</h1>
      <form onSubmit={add} className="mt-6 grid grid-cols-1 sm:grid-cols-5 gap-2">
        <select value={form.sport} onChange={e=>setForm(f=>({...f, sport:e.target.value}))} className="h-10 px-3 rounded-full border border-border bg-card"><option value="cricket">Cricket</option><option value="football">Football</option></select>
        <input placeholder="Teams" value={form.teams} onChange={e=>setForm(f=>({...f, teams:e.target.value}))} className="h-10 px-3 rounded-full border border-border bg-card sm:col-span-2" />
        <input placeholder="Status LIVE/FT" value={form.status} onChange={e=>setForm(f=>({...f, status:e.target.value}))} className="h-10 px-3 rounded-full border border-border bg-card" />
        <input placeholder="Details" value={form.details} onChange={e=>setForm(f=>({...f, details:e.target.value}))} className="h-10 px-3 rounded-full border border-border bg-card sm:col-span-5" />
        <button className="h-10 px-4 rounded-full bg-signal text-white font-bold text-sm sm:col-span-5">Add score</button>
      </form>
      <div className="mt-6 rounded-2xl bg-card border border-border divide-y divide-border">
        {list.map(s => <div key={s.id} className="p-3 flex items-center justify-between gap-3"><div><div className="text-xs text-muted-foreground uppercase">{s.sport} · {s.status}</div><div className="font-semibold">{s.teams} — {s.details}</div></div><button onClick={()=>del(s.id)} className="text-xs text-signal font-bold">Delete</button></div>)}
      </div>
    </div>
  );
};

const VideosMgmt = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", url: "", platform: "youtube", thumbnail: "" });
  const load = () => api.get("/videos").then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/admin/videos", form); setForm({ title:"", url:"", platform:"youtube", thumbnail:""}); load(); };
  const del = async (id) => { await api.delete(`/admin/videos/${id}`); load(); };
  return (
    <div data-testid="admin-videos">
      <h1 className="font-display font-extrabold text-3xl">Video embeds</h1>
      <form onSubmit={add} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input placeholder="Title" value={form.title} onChange={e=>setForm(f=>({...f, title:e.target.value}))} className="h-10 px-3 rounded-full border border-border bg-card sm:col-span-2" />
        <input placeholder="Embed URL (youtube/instagram/facebook)" value={form.url} onChange={e=>setForm(f=>({...f, url:e.target.value}))} className="h-10 px-3 rounded-full border border-border bg-card" />
        <select value={form.platform} onChange={e=>setForm(f=>({...f, platform:e.target.value}))} className="h-10 px-3 rounded-full border border-border bg-card">
          <option value="youtube">YouTube</option><option value="instagram">Instagram</option><option value="facebook">Facebook</option><option value="tiktok">TikTok</option>
        </select>
        <button className="h-10 px-4 rounded-full bg-signal text-white font-bold text-sm sm:col-span-2">Add video</button>
      </form>
      <div className="mt-6 rounded-2xl bg-card border border-border divide-y divide-border">
        {list.map(v => <div key={v.id} className="p-3 flex items-center justify-between gap-3"><span className="text-sm">{v.title} <span className="text-xs text-muted-foreground">({v.platform})</span></span><button onClick={()=>del(v.id)} className="text-xs text-signal font-bold">Delete</button></div>)}
      </div>
    </div>
  );
};

const Subscribers = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/subscribers").then(r => setList(r.data)); }, []);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <div data-testid="admin-subs">
      <div className="flex items-center justify-between"><h1 className="font-display font-extrabold text-3xl">Subscribers ({list.length})</h1>
        <a href={`${BACKEND}/api/admin/subscribers.csv`} target="_blank" rel="noreferrer" className="h-10 px-4 rounded-full bg-foreground text-background font-bold text-sm inline-flex items-center">Export CSV</a>
      </div>
      <div className="mt-6 rounded-2xl bg-card border border-border divide-y divide-border">
        {list.map(s => <div key={s.id} className="p-3 flex items-center justify-between gap-3"><span className="text-sm">{s.email}</span><span className="text-xs text-muted-foreground">{s.language} · {s.categories?.join(", ") || "all"}</span></div>)}
      </div>
    </div>
  );
};

const Inbox = ({ endpoint, title, fields, testId }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get(endpoint).then(r => setList(r.data)); }, [endpoint]);
  return (
    <div data-testid={testId}>
      <h1 className="font-display font-extrabold text-3xl">{title}</h1>
      <div className="mt-6 rounded-2xl bg-card border border-border divide-y divide-border">
        {list.length === 0 && <div className="p-6 text-center text-muted-foreground text-sm">Nothing here yet.</div>}
        {list.map(x => (
          <div key={x.id} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold">{fields.map(f => x[f]).filter(Boolean).join(" · ")}</div>
              <div className="text-xs text-muted-foreground">{new Date(x.created_at).toLocaleDateString()}</div>
            </div>
            {x.message && <p className="text-sm mt-1 whitespace-pre-wrap text-muted-foreground">{x.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const Analytics = () => {
  const [a, setA] = useState({});
  useEffect(() => { api.get("/admin/analytics").then(r => setA(r.data)); }, []);
  return (
    <div data-testid="admin-analytics">
      <h1 className="font-display font-extrabold text-3xl">Analytics</h1>
      <h2 className="font-display font-bold text-xl mt-6">By category</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        {(a.by_category||[]).map(c => <div key={c.name} className="rounded-2xl p-4 text-white" style={{ backgroundColor: c.color }}><div className="text-xs font-bold uppercase">{c.name}</div><div className="font-display font-extrabold text-2xl">{c.count}</div></div>)}
      </div>
      <h2 className="font-display font-bold text-xl mt-10">By source (last 7 days)</h2>
      <div className="mt-3 rounded-2xl bg-card border border-border divide-y divide-border">
        {(a.by_source||[]).map(s => (
          <div key={s.name} className="p-3">
            <div className="flex items-center justify-between text-sm"><span className="font-semibold">{s.name}</span><span className="font-bold">{s.pct}%</span></div>
            <div className="h-2 rounded-full bg-muted mt-2 overflow-hidden"><div className="h-full bg-signal" style={{ width: `${s.pct*2.5}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminRoutes = () => (
  <Routes>
    <Route path="/dashboard" element={<AdminShell><Dashboard /></AdminShell>} />
    <Route path="/stories" element={<AdminShell><StoriesList /></AdminShell>} />
    <Route path="/stories/:id" element={<AdminShell><StoryEditorWrap /></AdminShell>} />
    <Route path="/media" element={<AdminShell><MediaMgmt /></AdminShell>} />
    <Route path="/scores" element={<AdminShell><ScoresMgmt /></AdminShell>} />
    <Route path="/videos" element={<AdminShell><VideosMgmt /></AdminShell>} />
    <Route path="/subscribers" element={<AdminShell><Subscribers /></AdminShell>} />
    <Route path="/tips" element={<AdminShell><Inbox endpoint="/admin/tips" title="Tips inbox" fields={["name","contact","link"]} testId="admin-tips" /></AdminShell>} />
    <Route path="/ads" element={<AdminShell><Inbox endpoint="/admin/ad-inquiries" title="Ad inquiries" fields={["name","email","company","budget"]} testId="admin-ads" /></AdminShell>} />
    <Route path="/analytics" element={<AdminShell><Analytics /></AdminShell>} />
  </Routes>
);
